"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Mail, ShieldCheck, KeyRound, Loader2, ArrowLeft } from "lucide-react";

export default function ForgotPasswordPage() {
  const router = useRouter();
  
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Step 1: Send OTP
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/api/auth/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) setStep(2);
      else alert("Failed to send OTP. Please check your email.");
    } catch (error) {
      alert("Error sending OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/api/auth/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });
      if (res.ok) setStep(3);
      else alert("Invalid OTP.");
    } catch (error) {
      alert("Error verifying OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, newPassword }),
      });
      if (res.ok) {
        alert("Password reset successfully! You can now login.");
        router.push("/login");
      } else {
        alert("Failed to reset password.");
      }
    } catch (error) {
      alert("Error resetting password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        
        <div className="text-center">
          <Link href="/login" className="inline-flex items-center text-blue-600 hover:text-blue-500 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
          </Link>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Reset Password</h2>
        </div>

        {/* STEP 1: ENTER EMAIL */}
        {step === 1 && (
          <form onSubmit={handleSendOtp} className="space-y-6">
            <div className="relative">
              <Mail className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
              <input 
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" 
                placeholder="Enter your registered email" 
              />
            </div>
            <button type="submit" disabled={loading} className="w-full py-2 px-4 text-white bg-blue-600 rounded-md hover:bg-blue-700">
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Send OTP"}
            </button>
          </form>
        )}

        {/* STEP 2: ENTER OTP */}
        {step === 2 && (
          <form onSubmit={handleVerifyOtp} className="space-y-6">
            <div className="relative">
              <ShieldCheck className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
              <input 
                type="text" required value={otp} onChange={(e) => setOtp(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" 
                placeholder="Enter 6-digit OTP" 
              />
            </div>
            <button type="submit" disabled={loading} className="w-full py-2 px-4 text-white bg-green-600 rounded-md hover:bg-green-700">
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Verify OTP"}
            </button>
          </form>
        )}

        {/* STEP 3: NEW PASSWORD */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div className="relative">
              <KeyRound className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
              <input 
                type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500" 
                placeholder="Enter New Password" 
              />
            </div>
            <button type="submit" disabled={loading} className="w-full py-2 px-4 text-white bg-blue-600 rounded-md hover:bg-blue-700">
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Update Password"}
            </button>
          </form>
        )}

      </div>
    </div>
  );
}